import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { MapPin, Sparkles, TrendingUp, PlusCircle, Crown } from "lucide-react";
import VideoPlayer from "../components/VideoPlayer";
import { useAuth } from "../context/AuthContext";

function ProgressBar({ raised, goal }) {
  const pct = Math.min(100, Math.max(0, (raised / (goal || 1)) * 100));
  return (
    <div>
      <div className="flex justify-between text-xs uppercase tracking-widest text-bdu-cream/60 mb-2">
        <span>${Number(raised || 0).toLocaleString()} raised</span>
        <span>${Number(goal || 0).toLocaleString()} goal</span>
      </div>
      <div className="h-2 bg-bdu-surface2 border border-bdu-gold/20 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-bdu-gold to-amber-300 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-right text-xs text-bdu-gold mt-1">{pct.toFixed(1)}% funded</div>
    </div>
  );
}

export default function Founders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/founders").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-16">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="label-eyebrow" style={{ color: "rgba(212,175,55,0.9)" }}>Founders Circle</div>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl text-bdu-cream leading-[1.05] tracking-tight">
            Watch the next generation<br /><span className="italic text-bdu-gold">build in real time.</span>
          </h1>
          <p className="mt-6 text-bdu-cream/70 text-lg leading-relaxed">
            Young Black entrepreneurs with real ideas, real plans, and no capital.
            Watch their pitch. Fund their launch. Follow their journey.
            Every founder is vetted, every dollar is tracked.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={user && user !== false ? "/founders/submit" : "/register?next=/founders/submit"} className="btn-gold" data-testid="founders-apply-btn">
              <Sparkles size={16} /> Apply to Be Featured
            </Link>
            <Link to="/pledge" className="btn-ghost">Donate to the Fund</Link>
          </div>
          <p className="mt-3 text-xs text-bdu-cream/50" data-testid="founders-fee-hint">
            $100 Membership + $200 Admin Fee = $300 one-time to submit a pitch.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {loading ? (
          <div className="text-bdu-cream/50 py-16 text-center">Loading founders...</div>
        ) : items.length === 0 ? (
          <div className="relative overflow-hidden border border-bdu-gold/25 py-24 px-8 text-center" data-testid="founders-empty">
            <div className="absolute inset-0" style={{
              background: "radial-gradient(circle at center, rgba(212,175,55,0.08) 0%, rgba(0,0,0,0) 70%)"
            }} />
            <div className="relative z-10 max-w-xl mx-auto">
              <TrendingUp size={40} className="mx-auto text-bdu-gold mb-6" strokeWidth={1.25} />
              <h3 className="font-serif text-4xl text-bdu-cream">Founders Circle Launching Soon</h3>
              <p className="mt-4 text-bdu-cream/70">
                Be the first entrepreneur featured. Submit your pitch video, business plan, and funding need — vetted founders go live to our donor community.
              </p>
              <Link to={user && user !== false ? "/founders/submit" : "/register?next=/founders/submit"} className="btn-gold mt-8 inline-flex" data-testid="founders-empty-apply">
                <PlusCircle size={16} /> Submit Your Pitch
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((f) => (
              <article key={f.id} className="bdu-card flex flex-col" data-testid={`founder-card-${f.id}`}>
                <div className="mb-4">
                  <VideoPlayer url={f.video_url} posterUrl={f.photo_url} testId={`founder-video-${f.id}`} />
                </div>
                <div className="flex items-start gap-3">
                  {f.photo_url && (
                    <img src={f.photo_url} alt={f.name} className="w-12 h-12 rounded-full object-cover border border-bdu-gold/40" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="label-eyebrow">{f.category}</div>
                    <h3 className="mt-1 font-serif text-2xl text-bdu-cream leading-tight">{f.business_name}</h3>
                    <div className="mt-1 text-xs text-bdu-cream/50">by {f.name}{f.age ? `, ${f.age}` : ""}</div>
                    <span
                      className="mt-2 inline-flex items-center gap-1 border border-bdu-gold/50 bg-bdu-gold/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-bdu-gold"
                      data-testid={`founder-member-badge-${f.id}`}
                      title="Founding Member — paid $300 pitch access"
                    >
                      <Crown size={9} /> Founding Member
                    </span>
                  </div>
                </div>
                {f.tagline && (
                  <div className="mt-4 text-bdu-gold/90 italic font-serif">{f.tagline}</div>
                )}
                <p className="mt-3 text-sm text-bdu-cream/70 line-clamp-3 flex-1">{f.description}</p>
                <div className="mt-4 flex items-center gap-1 text-xs text-bdu-cream/50">
                  <MapPin size={12} /> {f.city}, {f.state}
                </div>
                <div className="mt-6">
                  <ProgressBar raised={f.raised_dollars} goal={f.goal_dollars} />
                </div>
                <Link to={`/founders/${f.id}`} className="btn-gold mt-6 w-full justify-center" data-testid={`founder-view-${f.id}`}>
                  View Full Pitch
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
